import { type FC } from "react";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { useCreatePlatformMenuItemMutation } from "@/partner/entities/platformMenuItem";
import { Dialog, useMediaQuery, useTheme } from "@mui/material";
import { MenuItemForm } from "./MenuItemForm";
import type { PlatformMenuItemFormValues } from "../model";

interface CreateMenuItemDialogProps {
  open: boolean;
  index?: number;
  onClose: () => void;
}

export const CreateMenuItemDialog: FC<CreateMenuItemDialogProps> = ({
  open,
  index,
  onClose,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const [createPlatformMenuItem, { loading }] =
    useCreatePlatformMenuItemMutation({
      onCompleted: () => {
        enqueueSnackbar(t("platformMenu.snackbar.created"), {
          variant: "success",
        });
        onClose();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
      refetchQueries: ["PlatformMenuItemsList"],
    });

  const handleSubmit = async (values: PlatformMenuItemFormValues) => {
    await createPlatformMenuItem({
      variables: {
        input: {
          ...values,
          index,
        },
      },
    });
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      fullScreen={fullScreen}
      scroll="body"
      open={open}
      onClose={onClose}
    >
      <MenuItemForm
        title={t("platformMenu.create.title")}
        loading={loading}
        onSubmit={handleSubmit}
        onClose={onClose}
      />
    </Dialog>
  );
};
